import React, { useState, useEffect } from "react";
import { Form, Row, Col, Button, Alert } from "react-bootstrap";
import { FaCheck, FaTimes } from "react-icons/fa";
import { fetchModelsByBrand, fetchEquipmentByModel } from "./EquipmentService";

const EquipmentAssignForm = ({
                                 assignmentData,
                                 setAssignmentData,
                                 categories,
                                 makes,
                                 handleAssignmentSave,
                                 handleCancel,
                                 error,
                                 hasAssetsAvailable = true,
                                 isAvailableStatus,
                                 t
                             }) => {
    const [availableModels, setAvailableModels] = useState([]);
    const [availableEquipments, setAvailableEquipments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [noEquipmentAvailable, setNoEquipmentAvailable] = useState(false);

    // Helper function to get field styles for required fields
    const getFieldStyle = (fieldName) => {
        const hasError = error?.[fieldName];
        const isEmpty = !assignmentData[fieldName] || assignmentData[fieldName].toString().trim() === '';

        if (hasError || isEmpty) {
            return {
                border: '2px solid #dc3545',
                borderRadius: '0.375rem'
            };
        }
        return {};
    };

    // When make changes, fetch related models
    useEffect(() => {
        const loadModels = async () => {
            if (!assignmentData.make || !hasAssetsAvailable) {
                setAvailableModels([]);
                return;
            }

            try {
                setLoading(true);
                const models = await fetchModelsByBrand(assignmentData.make);

                // Extract unique model names
                const uniqueModels = [...new Set(models.map(item => item.model))];
                setAvailableModels(uniqueModels);
            } catch (error) {
                console.error("Error fetching models:", error);
                // Don't show error to user, just log it
                setAvailableModels([]);
            } finally {
                setLoading(false);
            }
        };

        loadModels();
    }, [assignmentData.make, hasAssetsAvailable]);

    // When model changes, fetch equipment details
    useEffect(() => {
        const loadEquipmentDetails = async () => {
            if (!assignmentData.make || !assignmentData.model || !hasAssetsAvailable) {
                setAvailableEquipments([]);
                setNoEquipmentAvailable(false);
                setAssignmentData(prev => ({
                    ...prev,
                    equipment_id: "",
                    serial_number: ""
                }));
                return;
            }

            try {
                setLoading(true);
                setNoEquipmentAvailable(false);

                // Get equipment from backend API - already filtered for availability
                const equipments = await fetchEquipmentByModel(assignmentData.make, assignmentData.model);
                console.log("Available equipment for selection:", equipments);

                setAvailableEquipments(equipments);

                // If no equipment found, show error message
                if (!equipments || equipments.length === 0) {
                    console.log("No equipment available for this model");
                    setNoEquipmentAvailable(true);
                    setAssignmentData(prev => ({
                        ...prev,
                        equipment_id: "",
                        serial_number: ""
                    }));
                    return;
                }

                // Reset selection - let user select from dropdown
                setAssignmentData(prev => ({
                    ...prev,
                    equipment_id: "",
                    serial_number: "",
                    device_condition: prev.device_condition || "Good"
                }));

            } catch (error) {
                console.error("Error fetching equipment details:", error);
                setNoEquipmentAvailable(true);
                setAssignmentData(prev => ({
                    ...prev,
                    equipment_id: "",
                    serial_number: ""
                }));
            } finally {
                setLoading(false);
            }
        };

        loadEquipmentDetails();
    }, [assignmentData.make, assignmentData.model, hasAssetsAvailable, setAssignmentData]);

    const handleCategoryChange = (e) => {
        const category = e.target.value;
        setAssignmentData(prev => ({
            ...prev,
            category,
            make: "",
            model: "",
            serial_number: "",
            equipment_id: ""
        }));
    };

    const handleMakeChange = (e) => {
        const make = e.target.value;
        setAssignmentData(prev => ({
            ...prev,
            make,
            model: "",
            serial_number: "",
            equipment_id: ""
        }));
    };

    const handleModelChange = (e) => {
        const model = e.target.value;
        setAssignmentData(prev => ({
            ...prev,
            model,
            serial_number: "",
            equipment_id: ""
        }));
    };

    // Handle serial number selection from dropdown
    const handleSerialNumberChange = (e) => {
        const serialNumber = e.target.value;

        // Find the matching equipment to get its ID
        const selectedEquipment = availableEquipments.find(
            eq => eq.serial_number === serialNumber
        );

        if (selectedEquipment) {
            console.log("Selected equipment:", selectedEquipment);

            // Set the equipment data for assignment
            setAssignmentData(prev => ({
                ...prev,
                serial_number: serialNumber,
                equipment_id: selectedEquipment.id.toString(),
                device_condition: prev.device_condition || selectedEquipment.status || "Good"
            }));
        } else {
            // Reset if somehow no equipment matches
            setAssignmentData(prev => ({
                ...prev,
                serial_number: "",
                equipment_id: ""
            }));
        }
    };

    const handleDeviceConditionChange = (e) => {
        const condition = e.target.value;
        setAssignmentData(prev => ({
            ...prev,
            device_condition: condition
        }));
    };

    const handleDateChange = (e) => {
        setAssignmentData(prev => ({
            ...prev,
            date_assigned: e.target.value
        }));
    };

    const handleDeliveredByChange = (e) => {
        setAssignmentData(prev => ({
            ...prev,
            delivered_by: e.target.value
        }));
    };

    const handleDeliveryTypeChange = (e) => {
        setAssignmentData(prev => ({
            ...prev,
            delivery_type: e.target.value
        }));
    };

    return (
        <div className="bg-white p-4">
            <h4 className="mb-4">{t('assignEquipment') || "Assign Equipment"}</h4>

            {!hasAssetsAvailable && (
                <Alert variant="warning" className="mb-3">
                    No asset data available. Please add assets before assigning equipment.
                </Alert>
            )}

            <Form>
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="delivery_type">
                            <Form.Label>
                                {t('deliveryType') || "Delivery Type"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Select
                                value={assignmentData.delivery_type || ""}
                                onChange={handleDeliveryTypeChange}
                                isInvalid={!!error?.delivery_type}
                                style={getFieldStyle('delivery_type')}
                            >
                                <option value="">{t('selectDeliveryTypeOption') || "Select Delivery Type"}</option>
                                <option value="Assignment">Assignment</option>
                                <option value="Loan">Loan</option>
                            </Form.Select>
                            {error?.delivery_type && (
                                <Form.Control.Feedback type="invalid">
                                    {error.delivery_type}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="category">
                            <Form.Label>
                                {t('category') || "Category"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Select
                                value={assignmentData.category || ""}
                                onChange={handleCategoryChange}
                                isInvalid={!!error?.category}
                                disabled={!hasAssetsAvailable}
                                style={getFieldStyle('category')}
                            >
                                <option value="">{t('selectCategory') || "Select Category"}</option>
                                {categories.map((category) => (
                                    <option key={category} value={category}>
                                        {category}
                                    </option>
                                ))}
                            </Form.Select>
                            {error?.category && (
                                <Form.Control.Feedback type="invalid">
                                    {error.category}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="make">
                            <Form.Label>
                                {t('make') || "Make"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Select
                                value={assignmentData.make || ""}
                                onChange={handleMakeChange}
                                isInvalid={!!error?.make}
                                disabled={!assignmentData.category || !hasAssetsAvailable}
                                style={getFieldStyle('make')}
                            >
                                <option value="">{t('selectMake') || "Select Make"}</option>
                                {makes.map((make) => (
                                    <option key={make} value={make}>
                                        {make}
                                    </option>
                                ))}
                            </Form.Select>
                            {error?.make && (
                                <Form.Control.Feedback type="invalid">
                                    {error.make}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="model">
                            <Form.Label>
                                {t('model') || "Model"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Select
                                value={assignmentData.model || ""}
                                onChange={handleModelChange}
                                isInvalid={!!error?.model}
                                disabled={!assignmentData.make || loading || !hasAssetsAvailable}
                                style={getFieldStyle('model')}
                            >
                                <option value="">{loading ? "Loading..." : (t('selectModel') || "Select Model")}</option>
                                {availableModels.map((model) => (
                                    <option key={model} value={model}>
                                        {model}
                                    </option>
                                ))}
                            </Form.Select>
                            {error?.model && (
                                <Form.Control.Feedback type="invalid">
                                    {error.model}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="serial_number">
                            <Form.Label>
                                {t('serialNumber') || "Serial Number"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Select
                                value={assignmentData.serial_number || ""}
                                onChange={handleSerialNumberChange}
                                isInvalid={!!error?.serial_number || !!error?.equipment_id}
                                disabled={!assignmentData.model || loading || !hasAssetsAvailable || noEquipmentAvailable || availableEquipments.length === 0}
                                style={getFieldStyle('serial_number')}
                            >
                                <option value="">{loading ? "Loading..." : (t('selectSerialNumber') || "Select Serial Number")}</option>
                                {availableEquipments.map((equipment) => (
                                    <option key={equipment.id} value={equipment.serial_number}>
                                        {equipment.serial_number}
                                    </option>
                                ))}
                            </Form.Select>
                            {(error?.serial_number || error?.equipment_id) && (
                                <div className="text-danger">
                                    {error.serial_number || error.equipment_id}
                                </div>
                            )}
                            {noEquipmentAvailable && assignmentData.model && !loading && (
                                <div className="text-warning mt-1">
                                    <small>No available equipment for this model. Please select a different model.</small>
                                </div>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="device_condition">
                            <Form.Label>
                                {t('deviceCondition') || "Device Condition"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Select
                                name="device_condition"
                                value={assignmentData.device_condition || ""}
                                onChange={handleDeviceConditionChange}
                                isInvalid={!!error?.device_condition}
                                style={getFieldStyle('device_condition')}
                            >
                                <option value="">{t('selectDeviceCondition') || "Select Device Condition"}</option>
                                <option value="New">New</option>
                                <option value="Good">Good</option>
                                <option value="Fair">Fair</option>
                                <option value="Poor">Poor</option>
                                <option value="Defective">Defective</option>
                            </Form.Select>
                            {error?.device_condition && (
                                <Form.Control.Feedback type="invalid">
                                    {error.device_condition}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group controlId="date_assigned">
                            <Form.Label>
                                {t('dateAssigned') || "Date Assigned"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Control
                                type="date"
                                value={assignmentData.date_assigned || ""}
                                onChange={handleDateChange}
                                isInvalid={!!error?.date_assigned}
                                style={getFieldStyle('date_assigned')}
                            />
                            {error?.date_assigned && (
                                <Form.Control.Feedback type="invalid">
                                    {error.date_assigned}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group controlId="delivered_by">
                            <Form.Label>
                                {t('deliveredBy') || "Delivered By"}
                                <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                value={assignmentData.delivered_by || ""}
                                onChange={handleDeliveredByChange}
                                isInvalid={!!error?.delivered_by}
                                style={getFieldStyle('delivered_by')}
                            />
                            {error?.delivered_by && (
                                <Form.Control.Feedback type="invalid">
                                    {error.delivered_by}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                </Row>
            </Form>

            <div className="mt-4 pt-3 border-top">
                <div className="d-flex justify-content-end">
                    <Button
                        variant="success"
                        onClick={handleAssignmentSave}
                        className="me-2"
                        disabled={loading || !assignmentData.equipment_id || !assignmentData.serial_number || noEquipmentAvailable}
                    >
                        <FaCheck /> {t('assign') || "Assign"}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleCancel}
                        disabled={loading}
                    >
                        <FaTimes /> {t('cancel') || "Cancel"}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default EquipmentAssignForm;